import React from 'react';
import { Link } from 'react-router-dom';
import content from './content.json';
import Dither from './Dither';
import { BASE, DISCORD, MAIL } from './Layout';
import { LogEntry } from './Log';
import { ArticleCard } from './Research';

const AXES = [
  { n: '01', title: 'Body replacement', text: 'Synthetic limbs, organs and physical systems that exceed the performance of biology.', nodes: ['limbs', 'organs', 'actuation', 'materials'] },
  { n: '02', title: 'Brain–computer interfaces', text: 'Direct input and output for the brain. Interfaces for control, cognition and communication.', nodes: ['input', 'output', 'control', 'communication'] },
  { n: '03', title: 'Sensory addition', text: 'New perceptual layers. Adding to vision, sensing the environment, feeding the body data it has no organ for.', nodes: ['vision', 'environment', 'new senses'] },
];

const FOCUS = [
  { n: '01', stage: 'Collaborator assembly', text: 'Building a network of researchers, engineers and experimentalists across disciplines.' },
  { n: '02', stage: 'Early systems design', text: 'Writing specifications for a first system on each axis. Prototyping only what we can instrument.' },
  { n: '03', stage: 'Research direction', text: 'Defining long-term trajectories and the methodology the lab works by.' },
];

const STATS = [
  { value: '3', label: 'Research axes', text: 'Motion, perception, cognition. Everything else is downstream.' },
  { value: '0', label: 'Products', text: 'A research project, not a company. No roadmap to a seed round.' },
  { value: 'v1.0', label: 'Current body version', text: 'In production for ~300,000 years. No patch notes.' },
];

function Schematic() {
  return (
    <svg viewBox="0 0 1200 176" className="hidden md:block w-full mt-16 text-[var(--ibl-ink)]" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.2">
      <rect x="562" y="6" width="76" height="76" rx="20" />
      <path d="M600 30v28M586 44h28" strokeWidth="2" strokeLinecap="round" />
      <path d="M600 112v16M193 128h814M193 128v48M600 128v48M1007 128v48" opacity="0.3" />
      <path className="ibl-dash" d="M600 112v16M600 128H193v48M600 128v48M600 128h407v48" opacity="0.8" />
      <circle cx="193" cy="128" r="3.5" fill="var(--ibl-bg)" /><circle cx="600" cy="128" r="3.5" fill="var(--ibl-bg)" /><circle cx="1007" cy="128" r="3.5" fill="var(--ibl-bg)" />
    </svg>
  );
}

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="ibl-grid absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-6 pt-16 sm:pt-24 pb-10 text-center">
          <p className="ibl-mono ibl-muted">Research collective for human augmentation</p>
          <h1 className="sr-only">The body is a draft.</h1>
          <div className="ibl-doto mt-8 text-[min(11.5vw,132px)]" aria-hidden="true">THE BODY</div>
          <figure className="relative mt-3 sm:mt-5 h-[32vh] min-h-[200px] max-h-[360px] text-[var(--ibl-ink)]">
            <Dither />
          </figure>
          <div className="ibl-doto mt-4 sm:mt-6 text-[min(11.5vw,132px)]" aria-hidden="true">IS A DRAFT.</div>
          <p className="mt-10 mx-auto max-w-xl text-[18px] sm:text-[20px] ibl-muted leading-relaxed">
            Iteration Body Labs studies what the human body could become. Motion, perception, cognition, treated as engineering problems. Not treatment. Not spectacle.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to={`${BASE}/research`} className="ibl-btn"><span className="ibl-dots" />Read the direction</Link>
            <a href="#join" className="ibl-btn ibl-btn-ghost">Join the lab</a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-6xl px-6 mt-16">
        <div className="grid sm:grid-cols-3 rounded-2xl border ibl-line bg-[rgba(255,255,255,0.35)] divide-y sm:divide-y-0 sm:divide-x divide-[var(--ibl-line)]">
          {STATS.map((s) => (
            <div key={s.label} className="p-7 sm:p-8">
              <div className="ibl-doto text-[56px]">{s.value}</div>
              <div className="mt-3 text-[15px] font-medium">{s.label}</div>
              <div className="mt-1 text-[13px] ibl-muted leading-relaxed">{s.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What we do */}
      <section className="mx-auto max-w-6xl px-6 mt-32">
        <p className="ibl-mono ibl-muted mb-6">What we do</p>
        <h2 className="text-[32px] sm:text-[48px] font-medium tracking-[-0.035em] leading-[1.05] max-w-4xl">
          We study what the body could become. <span className="ibl-faint">Any technology that changes how it moves, feels or thinks, treated as engineering.</span>
        </h2>
        <p className="mt-8 max-w-xl text-[17px] ibl-muted leading-relaxed">
          Not for treatment. Not for spectacle. The long-term goal is a research space like Bell Labs, dedicated entirely to re-engineering the human form.
        </p>
        <Schematic />
        <div className="grid md:grid-cols-3 gap-4 md:gap-5 mt-6 md:mt-0">
          {AXES.map((a) => (
            <div key={a.n} className="rounded-2xl border ibl-line p-7 bg-[rgba(255,255,255,0.3)] hover:bg-[rgba(255,255,255,0.55)] transition-colors">
              <h3 className="text-[22px] font-medium tracking-[-0.02em] leading-tight">{a.title}</h3>
              <p className="mt-3 text-[14px] ibl-muted leading-relaxed">{a.text}</p>
              <div className="mt-6 flex flex-wrap gap-2">{a.nodes.map((n) => <span key={n} className="ibl-chip">{n}</span>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Current focus */}
      <section className="mx-auto max-w-6xl px-6 mt-32">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20">
          <div>
            <p className="ibl-mono ibl-muted mb-6">Current focus</p>
            <h2 className="text-[32px] sm:text-[40px] font-medium tracking-[-0.03em] leading-[1.05]">Early stage, <span className="ibl-faint">on purpose.</span></h2>
            <p className="mt-6 text-[15px] ibl-muted leading-relaxed">
              Iteration is assembling collaborators, designing early systems and writing down how it works before it builds anything it cannot measure.
            </p>
          </div>
          <ol className="border-t ibl-line">
            {FOCUS.map((f) => (
              <li key={f.n} className="grid grid-cols-[44px_1fr] gap-4 py-6 border-b ibl-line items-baseline">
                <span className="ibl-doto text-[22px]">{f.n}</span>
                <div>
                  <h3 className="text-[19px] font-medium tracking-[-0.01em]">{f.stage}</h3>
                  <p className="mt-1.5 text-[14px] ibl-muted leading-relaxed">{f.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Log */}
      <section className="mx-auto max-w-6xl px-6 mt-32">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="ibl-mono ibl-muted mb-4">Log</p>
            <h2 className="text-[32px] sm:text-[40px] font-medium tracking-[-0.03em]">Latest entries</h2>
          </div>
          <Link to={`${BASE}/log`} className="ibl-mono text-[10px] hover:underline underline-offset-4 whitespace-nowrap">All {content.news.length} entries →</Link>
        </div>
        <div className="mt-8 border-b ibl-line">
          {content.news.slice(0, 3).map((e) => <LogEntry key={e.date + e.slug} entry={e} />)}
        </div>
      </section>

      {/* Research */}
      <section className="mx-auto max-w-6xl px-6 mt-32">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="ibl-mono ibl-muted mb-4">Research</p>
            <h2 className="text-[32px] sm:text-[40px] font-medium tracking-[-0.03em]">Direction</h2>
          </div>
          <Link to={`${BASE}/research`} className="ibl-mono text-[10px] hover:underline underline-offset-4 whitespace-nowrap">All articles →</Link>
        </div>
        <div className="mt-8 border-b ibl-line">
          {content.articles.slice(0, 2).map((a) => <ArticleCard key={a.slug} article={a} />)}
        </div>
      </section>

      {/* Join */}
      <section id="join" className="mx-auto max-w-6xl px-6 mt-32 scroll-mt-28">
        <div className="ibl-dark rounded-3xl bg-[var(--ibl-ink)] text-[var(--ibl-bg)] p-8 sm:p-14">
          <div>
            <p className="ibl-mono opacity-60 mb-6">Open call</p>
            <h2 className="text-[36px] sm:text-[56px] font-medium tracking-[-0.035em] leading-[1]">Call for collaborators.</h2>
            <p className="mt-7 text-[17px] opacity-80 leading-relaxed max-w-xl">
              Engineers, researchers, designers, experimentalists. Robotics, neuroscience, prosthetics, biodesign, adjacent fields, or a student who cannot stop thinking about this.
            </p>
            <p className="mt-4 text-[15px] opacity-60 max-w-xl">This is a research project, not a company. If the ambition resonates, reach out.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={MAIL} className="ibl-btn"><span className="ibl-dots" />Write to the lab</a>
              <a href={DISCORD} target="_blank" rel="noopener noreferrer" className="ibl-btn ibl-btn-ghost">Join the Discord ↗</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
